import { CheckCircle2, ExternalLink } from "lucide-react";
import { NEWS_COLORS } from "../constants/news.constants";
import { conclaveArticle } from "../data/newsContent";
import { NewsMeta, NewsTag } from "./NewsTag";
import { NewsVideoCard } from "./NewsVideoCard";

export function NewsArticleConclave() {
  const a = conclaveArticle;

  return (
    <article
      id={a.id}
      className="scroll-mt-24"
      itemScope
      itemType="https://schema.org/NewsArticle"
    >
      <meta itemProp="datePublished" content="2026-07-26T10:00:00+05:30" />
      <meta itemProp="dateModified" content="2026-07-26T10:00:00+05:30" />
      <meta itemProp="image" content={a.videoPoster} />
      <span itemProp="author" itemScope itemType="https://schema.org/Person" className="hidden">
        <span itemProp="name">{a.founder.name}</span>
        <span itemProp="jobTitle">{a.founder.role}</span>
      </span>
      <span itemProp="publisher" itemScope itemType="https://schema.org/Organization" className="hidden">
        <span itemProp="name">Duri Ridesharing</span>
      </span>

      {/* —— Top Hero Grid: Story + Video Card —— */}
      <div className="grid gap-8 lg:grid-cols-[1.12fr_0.88fr] lg:items-start lg:gap-10">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <NewsTag>{a.tag}</NewsTag>
            <NewsMeta date={a.date} readTime={a.readTime} />
          </div>
          <h2 itemProp="headline" className="mt-4 text-2xl font-extrabold leading-snug tracking-tight text-gray-900 sm:text-3xl">
            {a.title}
          </h2>
          <p itemProp="description" className="mt-4 text-sm leading-relaxed text-gray-600 sm:text-base">
            {a.description}
          </p>

          <ul className="mt-5 space-y-2.5">
            {a.keyPoints.map((point) => (
              <li key={point} className="flex items-start gap-2.5 text-sm text-gray-700">
                <CheckCircle2
                  className="mt-0.5 h-4 w-4 shrink-0"
                  style={{ color: NEWS_COLORS.primary }}
                  aria-hidden
                />
                <span>{point}</span>
              </li>
            ))}
          </ul>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <a
              href={a.youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border-2 px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-[#E8F5F0]"
              style={{
                borderColor: NEWS_COLORS.primary,
                color: NEWS_COLORS.primary,
              }}
            >
              Watch Full Keynote on YouTube
              <ExternalLink className="h-4 w-4" aria-hidden />
            </a>
          </div>
        </div>

        <div>
          <NewsVideoCard
            label={a.videoLabel}
            duration={a.videoDuration}
            youtubeId={a.youtubeId}
            poster={a.videoPoster}
            tone="event"
          />

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-gray-500">
            <span className="flex items-center gap-1.5 font-medium text-gray-700">
              <CheckCircle2
                className="h-4 w-4"
                style={{ color: NEWS_COLORS.primary }}
              />
              Official 20-Min Keynote Address at T-Hub
            </span>
            <a
              href={a.youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-semibold transition-colors hover:underline"
              style={{ color: NEWS_COLORS.primary }}
            >
              Watch on YouTube &rarr;
            </a>
          </div>
        </div>
      </div>

      {/* —— Middle Full-Width Grid: Keynote Speech Insights —— */}
      <div className="mt-10 sm:mt-12">
        <p
          className="text-xs font-semibold uppercase tracking-wider"
          style={{ color: NEWS_COLORS.primary }}
        >
          Keynote Insights &amp; Strategic Pillars
        </p>
        <div className="mt-4 grid gap-5 md:grid-cols-3">
          {a.insights.map(({ title, description, Icon }) => (
            <div
              key={title}
              className="flex flex-col rounded-2xl border border-gray-100 p-5 shadow-xs transition-shadow hover:shadow-sm"
              style={{ backgroundColor: NEWS_COLORS.surface }}
            >
              <div className="flex items-center gap-3">
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                  style={{
                    backgroundColor: NEWS_COLORS.primarySoft,
                    color: NEWS_COLORS.primary,
                  }}
                >
                  <Icon className="h-5 w-5" aria-hidden />
                </span>
                <h3 className="text-sm font-bold text-gray-900">{title}</h3>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-gray-600 sm:text-sm">
                {description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* —— Bottom 2-Column Grid: Founder Context & Innovations —— */}
      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div
          className="flex flex-col justify-between rounded-2xl border border-gray-100 p-5 sm:p-6"
          style={{ backgroundColor: NEWS_COLORS.surface }}
        >
          <div>
            <p className="text-sm font-bold text-gray-900">
              {a.founder.name}
              <span className="font-normal text-gray-500">
                , {a.founder.role}
              </span>
            </p>
            <p className="mt-3 text-sm leading-relaxed text-gray-600">
              {a.founder.intro}
            </p>
          </div>

          <div className="mt-5 rounded-xl border border-[#1F7A5A]/15 bg-[#E8F5F0]/50 p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-[#1F7A5A]">
              100% Make In India Engineering
            </p>
            <p className="mt-1.5 text-xs leading-relaxed text-gray-700 sm:text-sm">
              {a.founder.engineeringNote}
            </p>
          </div>
        </div>

        <div>
          <p
            className="text-base font-bold sm:text-lg"
            style={{ color: NEWS_COLORS.primary }}
          >
            {a.featuresHeading}
          </p>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {a.features.map(({ title, Icon }) => (
              <li
                key={title}
                className="flex items-start gap-3 rounded-xl border border-gray-100 bg-white p-3.5 shadow-sm"
              >
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                  style={{
                    backgroundColor: NEWS_COLORS.primarySoft,
                    color: NEWS_COLORS.primary,
                  }}
                >
                  <Icon className="h-4 w-4" aria-hidden />
                </span>
                <p className="text-sm font-semibold leading-snug text-gray-800">
                  {title}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </article>
  );
}
